import {Component, OnInit} from '@angular/core';
import Utils from '@app/utils/utils';
import {environment} from '@env/environment';
import {ApiService} from '@app/api/api.service';
import {HttpClient} from '@angular/common/http';
import {NetworkTester} from '@app/health/network-tester';
import {NotificationService} from '@agilent/ui-element';

enum RegistrationThresholdsEnum {
    REQUIRED_DOWNLOAD = 2,
    REQUIRED_UPLOAD = 1,
    REQUIRED_LATENCY = 200,

    RECOMMENDED_DOWNLOAD = 5,
    RECOMMENDED_UPLOAD = 2,
    RECOMMENDED_LATENCY = 100
}

enum IOTThresholdsEnum {
    REQUIRED_DOWNLOAD = null,
    REQUIRED_UPLOAD = 1,
    REQUIRED_LATENCY = 4000,

    RECOMMENDED_DOWNLOAD = null,
    RECOMMENDED_UPLOAD = 2,
    RECOMMENDED_LATENCY = 1500
}

enum S3ThresholdsEnum {
    REQUIRED_DOWNLOAD = 15,
    REQUIRED_UPLOAD = null,
    REQUIRED_LATENCY = 300,

    RECOMMENDED_DOWNLOAD = 30,
    RECOMMENDED_UPLOAD = null,
    RECOMMENDED_LATENCY = 150
}

@Component({
    selector: 'app-health',
    templateUrl: './health.component.html',
    styleUrls: ['./health.component.scss']
})
export class HealthComponent implements OnInit {

    public productIcon;
    public buttonIcon;
    public loadingIcon;

    public results: {
        name: string, description: string, domain: string, entries: any[], tester: NetworkTester
    }[] = [];

    testRunning: boolean;

    registrationApiBaseUrl: string = null;
    iotEndpoint: string = null;
    hubUrl: string = null;
    dataTunnelingIotEndpoint: string = null;

    constructor(
        private apiService: ApiService,
        private http: HttpClient,
        private notificationService: NotificationService,
    ) {
        this.registrationApiBaseUrl = environment.registration_api_base_url;
        this.iotEndpoint = environment.iot_base_url;
        this.hubUrl = environment.cognito_redirect_uri_sign_out;
        this.dataTunnelingIotEndpoint = environment.data_tunneling_iot_endpoint;
    }

    async ngOnInit(): Promise<void> {
        await this.loadIcons();
        this.connectionTest();
    }

    public copyToClipboard(text: string) {
        if (!navigator.clipboard) {
            this.notificationService.warning(
                'Copy not supported in this browser');
        }
        navigator.clipboard.writeText(text).then( () => {
                this.notificationService.success(
                    'URL copied to clipboard');
            }
        );
    }

    public async connectionTest() {
        if (this.testRunning) {
            return; // Exit if test is already running
        }

        this.testRunning = true;
        this.results.length = 0;

        // Test CID HUB
        const cidHubTester = new NetworkTester(this.http, null, null, this.hubUrl)
        this.results.push({
            name: 'CID HUB',
            description: 'CID HUB landing page',
            domain: this.hubUrl,
            entries: this.createEntries('IoT', cidHubTester),
            tester: cidHubTester
        });
        cidHubTester.runConnectionTest(() => {

            // Test registration API
            const registrationTester = new NetworkTester(this.http, this.registrationApiBaseUrl + '/health', this.registrationApiBaseUrl + '/health');
            this.results.push({
                name: 'Registration API',
                description: 'These APIs are used by CIDs to communicate with the HUB and query for its configuration ' +
                    'and settings information. It also updates its status on the HUB using these APIs. ' +
                    'A low https response time is required to ensure good communication between the CID and the HUB. ' +
                    'A low https response time is also required for HUB web pages to load quickly.',
                domain: this.registrationApiBaseUrl,
                entries: this.createEntries('Registration', registrationTester),
                tester: registrationTester
            });
            registrationTester.runTest(() => {

                // Test IoT after the registration API
                const publish_url = this.iotEndpoint + '/topics/test?qos=1';
                const IoTTester = new NetworkTester(this.http, null, publish_url);
                this.results.push({
                    name: 'IoT',
                    description: 'Commands and tasks requested in the HUB pages (like "install a driver") are sent ' +
                        'to the corresponding CID via Agilent\'s AWS IoT core service. ' +
                        'CIDs connect to the IoT Core Service to look for messages meant for itself. ' +
                        'A lower https response time ensures that this communication is quicker.',
                    domain: this.iotEndpoint,
                    entries: this.createEntries('IoT', IoTTester),
                    tester: IoTTester,
                });

                IoTTester.runTest(async () => {

                    const dataTunnelingIotTester = new NetworkTester(this.http, null, null, this.dataTunnelingIotEndpoint)
                    this.results.push({
                        name: 'IoT Tunnel Data Endpoint',
                        description: 'Used by AWS IoT Secure Tunneling to enable remote access to CIDs.',
                        domain: this.dataTunnelingIotEndpoint,
                        entries: this.createEntries('IoT', dataTunnelingIotTester),
                        tester: dataTunnelingIotTester
                    });
                    dataTunnelingIotTester.runConnectionTest(async () => {
                        // Test S3 bucket last
                        let s3testUrlResponse;
                        let s3testUrl = null;
                        let s3bucketUrl = null;
                        // If we cannot obtain a pre-signed url, we cannot perform a test.
                        if (!registrationTester.reachable) {
                            this.notificationService.error('Cannot test S3 if Registration API is unreachable');
                        } else {
                            try {
                                s3testUrlResponse = await this.getData(this.registrationApiBaseUrl + '/s3_test_url');
                                s3testUrl = s3testUrlResponse['url'];
                                s3bucketUrl = s3testUrlResponse['bucket_url'];
                            } catch (e) {
                                console.log(e);
                                this.notificationService.error('Cannot test S3, unable to obtain pre-signed URL from the registration API');
                            }
                        }

                        const s3Tester = new NetworkTester(this.http, s3testUrl);
                        this.results.push({
                            name: 'S3 Bucket',
                            description: 'Files related to CDS, drivers, and Linux updates are hosted in ' +
                                'Agilent\'s AWS S3 bucket for CIDs. ' +
                                'Faster download speed is required to ensure timely retrieval of these files.',
                            domain: s3bucketUrl,
                            entries: this.createEntries('S3', s3Tester),
                            tester: s3Tester,
                        });

                        s3Tester.runTest(() => {
                            const powershellgalleryTester = new NetworkTester(this.http, null, null, 'https://www.powershellgallery.com');
                            this.results.push({
                                name: 'Powershell Gallery',
                                description: 'Used for downloading PowerShell modules for CID Windows updates.',
                                domain: 'https://www.powershellgallery.com',
                                entries: this.createEntries('', powershellgalleryTester),
                                tester: powershellgalleryTester
                            });

                            powershellgalleryTester.runConnectionTest(() => {
                                
                                const psgGetAzureTester = new NetworkTester(this.http, null, null, 'https://cdn.oneget.org/');

                                this.results.push({
                                    name: 'PowerShell Gallery CDN Endpoint',
                                    description: 'Hosts PowerShell Gallery packages and dependencies needed by update scripts during CID Windows maintenance.',
                                    domain: 'https://cdn.oneget.org',
                                    entries: this.createEntries('', psgGetAzureTester),
                                    tester: psgGetAzureTester
                                });
                                psgGetAzureTester.runConnectionTest(() => {
                                    const microsoftTester = new NetworkTester(this.http, null, null, 'https://microsoft.com');

                                    this.results.push({
                                        name: 'Microsoft Windows Updates',
                                        description: 'Required for accessing official Microsoft Windows Update services, activation, and validation for CIDs.',
                                        domain: 'https://microsoft.com',
                                        entries: this.createEntries('', microsoftTester),
                                        tester: microsoftTester
                                    });
                                    microsoftTester.runConnectionTest(() => {
                                        const devopsGalleryTester = new NetworkTester(this.http, null, null, 'https://devopsgallerystorage.blob.core.windows.net');

                                        this.results.push({
                                            name: 'Powershell DevOps Gallery Storage',
                                            description: 'Stores and delivers PowerShell Gallery packages that may be used in update-related scripts or tooling for CID.',
                                            domain: 'https://devopsgallerystorage.blob.core.windows.net',
                                            entries: this.createEntries('', devopsGalleryTester),
                                            tester: devopsGalleryTester
                                        });
                                        devopsGalleryTester.runConnectionTest(() => {
                                            this.testRunning = false;
                                        })
                                    })
                                });
                            });
                        });
                    });
                });
            });
        });
    }

    private createEntries(testUnit: string, tester: NetworkTester) {
        const entries = [];

        let requiredDownloadBandwidthThreshold;
        let recommendedDownloadBandwidthThreshold;

        let requiredUploadBandwidthThreshold;
        let recommendedUploadBandwidthThreshold;

        let requiredLatencyThreshold;
        let recommendedLatencyThreshold;

        switch(testUnit) {
            case 'Registration':
                requiredDownloadBandwidthThreshold = RegistrationThresholdsEnum.REQUIRED_DOWNLOAD;
                requiredUploadBandwidthThreshold = RegistrationThresholdsEnum.REQUIRED_UPLOAD;
                requiredLatencyThreshold = RegistrationThresholdsEnum.REQUIRED_LATENCY;

                recommendedDownloadBandwidthThreshold = RegistrationThresholdsEnum.RECOMMENDED_DOWNLOAD;
                recommendedUploadBandwidthThreshold = RegistrationThresholdsEnum.RECOMMENDED_UPLOAD;
                recommendedLatencyThreshold = RegistrationThresholdsEnum.RECOMMENDED_LATENCY;

                break;
            case 'IoT':
                requiredDownloadBandwidthThreshold = IOTThresholdsEnum.REQUIRED_DOWNLOAD;
                requiredUploadBandwidthThreshold = IOTThresholdsEnum.REQUIRED_UPLOAD;
                requiredLatencyThreshold = IOTThresholdsEnum.REQUIRED_LATENCY;

                recommendedDownloadBandwidthThreshold = IOTThresholdsEnum.RECOMMENDED_DOWNLOAD;
                recommendedUploadBandwidthThreshold = IOTThresholdsEnum.RECOMMENDED_UPLOAD;
                recommendedLatencyThreshold = IOTThresholdsEnum.RECOMMENDED_LATENCY;

                break;
            case 'S3':
                requiredDownloadBandwidthThreshold = S3ThresholdsEnum.REQUIRED_DOWNLOAD;
                requiredUploadBandwidthThreshold = S3ThresholdsEnum.REQUIRED_UPLOAD;
                requiredLatencyThreshold = S3ThresholdsEnum.REQUIRED_LATENCY;

                recommendedDownloadBandwidthThreshold = S3ThresholdsEnum.RECOMMENDED_DOWNLOAD;
                recommendedUploadBandwidthThreshold = S3ThresholdsEnum.RECOMMENDED_UPLOAD;
                recommendedLatencyThreshold = S3ThresholdsEnum.RECOMMENDED_LATENCY;

                break;
        }

        if (tester.downloadUrl !== null) {
            entries.push({
                name: 'Download speed',
                metric: 'Mbps',
                recommended: `(recommended > ${recommendedDownloadBandwidthThreshold} Mbps, required > ${requiredDownloadBandwidthThreshold} Mbps)`,
                value: () => tester.reachable && tester.averageDownloadBandwidth !== null ? tester.averageDownloadBandwidth.toFixed(2) : ' - ',
                isGreen: () => tester.averageDownloadBandwidth >= recommendedDownloadBandwidthThreshold,
                isYellow: () => tester.averageDownloadBandwidth >= requiredDownloadBandwidthThreshold
                    && tester.averageDownloadBandwidth < recommendedDownloadBandwidthThreshold,
                isRed: () => tester.averageDownloadBandwidth < requiredDownloadBandwidthThreshold
            });
        }

        if (tester.uploadUrl !== null) {
            entries.push({
                name: 'Upload speed',
                metric: 'Mbps',
                recommended: `(recommended > ${recommendedUploadBandwidthThreshold} Mbps, required > ${requiredUploadBandwidthThreshold} Mbps)`,
                value: () => tester.reachable && tester.averageUploadBandwidth !== null ? tester.averageUploadBandwidth.toFixed(2) : ' - ',
                isGreen: () => tester.averageUploadBandwidth >= recommendedUploadBandwidthThreshold,
                isYellow: () => tester.averageUploadBandwidth >= requiredUploadBandwidthThreshold
                    && tester.averageUploadBandwidth < recommendedUploadBandwidthThreshold,
                isRed: () => tester.averageUploadBandwidth < requiredUploadBandwidthThreshold
            });
        }

        if (tester.uploadUrl !== null || tester.downloadUrl !== null) {
            entries.push({
                name: 'Https Response Time',
                metric: 'ms',
                recommended: `(recommended < ${recommendedLatencyThreshold} ms, required < ${requiredLatencyThreshold} ms)`,
                value: () => tester.reachable && tester.averageLatency !== null ? tester.averageLatency.toFixed(0) : ' - ',
                isGreen: () => tester.averageLatency <= recommendedLatencyThreshold,
                isYellow: () => tester.averageLatency <= requiredLatencyThreshold
                    && tester.averageLatency > recommendedLatencyThreshold,
                isRed: () => tester.averageLatency > requiredLatencyThreshold
            });
        }

        return entries;
    }

    private async loadIcons() {
        this.productIcon = await Utils.getSvgByUrl('/assets/icons/cid_hub.svg');
        this.buttonIcon = await Utils.getSvgByUrl('/assets/icons/refresh.svg');
        this.loadingIcon = await Utils.getSvgByUrl('/assets/icons/diagnostics.svg');
    }

    private async getData(url: string) {
        let response;
        try {
            response = await fetch(url);
        } catch (AbortError) {
            return null;
        }

        if (!response.ok) {
            let respData = null;
            let respText = null;

            try {
                respData = await response.json();
            } catch (ex) {
                respText = await response.text();
            }

            return {
                data: respData, statusCode: response.status, text: respText
            };
        }

        const contentType = response.headers.get('content-type');
        // If we fetch an image, handle it appropriately
        if (contentType && contentType.indexOf('image/png') !== -1) {
            return await response.blob();
        }
        // Return response JSON
        return await response.json();
    }

}
